import { useEffect, useMemo, useState } from 'react';
import TicketBuilder from '../components/TicketBuilder';
import UploadGames from '../components/UploadGames';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Brain, Loader2, Ticket } from 'lucide-react';
import { GameCard, PickRow, legText } from '../components/SuggestionsTab';
import { useBetMode, plainLeg } from '../lib/betMode';
import BetModeSwitch from '../components/BetModeSwitch';
import PicksNotOnTickets from '../components/PicksNotOnTickets';
import { SafeSection, MlRow } from '../components/SafeTickets';
import { ticketIndex, dayLabel } from '../lib/sportsPicks';
import PregameStatus from '../components/PregameStatus';
import { GamesBySlot } from '../components/GamesBySlot';
import { InjuryTag } from '../components/InjuryTag';
import PicksCalendar from '../components/PicksCalendar';
import PickRulesPanel from '../components/PickRulesPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;

export const NbaWins = ({ wins }) => (
  wins?.length ? (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid="nba-wins">
      <p className="text-sm font-bold text-white mb-1">Team win picks</p>
      {wins.map((x, i) => <MlRow key={`${x.key}-${x.team}`} x={x} testId={`nba-win-${i}`} />)}
    </div>
  ) : null
);

const chance = (p) => <span className="shrink-0 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-xs font-bold text-emerald-300">{Math.round(p * 100)}% likely</span>;

// One ticket pick, written so someone who has never bet can follow it.
const NbaPlainLeg = ({ x, i }) => {
  const mode = useBetMode();
  if (mode === 'pro') return x.kind === 'ml' ? <MlRow x={x} testId={`nba-ticket-leg-${i}`} /> : <PickRow x={x} testId={`nba-ticket-leg-${i}`} />;
  if (x.kind === 'ml') return (
    <div className="py-2 border-t border-zinc-800 first:border-0" data-testid={`nba-ticket-leg-${i}`}>
      <div className="flex items-start gap-2"><img src={x.team_logo} alt="" className="w-6 h-6" />
        <p className="flex-1 font-bold text-white">{i + 1}. The {x.team} win the game</p>{chance(x.prob)}</div>
      <p className="text-[13px] text-zinc-300 mt-1 ml-8">Doesn't matter by how much — they just need to win. ({x.game})</p>
    </div>
  );
  const st = x.stats || {};
  const n = (st.last5 || []).length;
  return (
    <div className="py-2 border-t border-zinc-800 first:border-0" data-testid={`nba-ticket-leg-${i}`}>
      <div className="flex items-start gap-2"><img src={x.team_logo} alt="" className="w-6 h-6" />
        <p className="flex-1 font-bold text-white">{i + 1}. {plainLeg(x)} {x.injury && <InjuryTag injury={x.injury} testId={`nba-ticket-leg-${i}-injury`} withDetail />}</p>{chance(x.prob)}</div>
      <ul className="text-[13px] text-zinc-300 mt-1 ml-8 space-y-0.5">
        <li>He usually gets about <b className="text-white">{st.season_avg}</b> a game, and <b className="text-white">{st.last5_avg}</b> over his last {n} games.</li>
        {n > 0 && <li>He reached {x.mark} or more in <b className="text-emerald-300">{st.hits5} of his last {n}</b> games ({st.last5.map((v) => Math.round(v)).join(', ')}).</li>}
        <li>Our guess for tonight: about <b className="text-white">{x.projection}</b> — so {x.mark} leaves room for an off night.</li>
        <li className="text-zinc-500">{x.game} tonight</li>
      </ul>
    </div>
  );
};

// Up to 8 safest legs of the night (props + team wins), with one tap to track it.
const NbaSuggestedTicket = ({ legs, date }) => {
  const mode = useBetMode();
  if (!legs?.length) return null;
  const track = async () => {
    const out = legs.map((x, i) => (x.kind === 'ml'
      ? { id: `n${i}`, league: 'nba', bet_type: 'moneyline', selection: `${x.team} ML`, line: x.ml ? String(x.ml) : null }
      : { id: `n${i}`, league: 'nba', bet_type: 'player_prop', player_name: x.player, stat_target: x.stat, selection: legText(x), line: `${x.mark}+` }));
    try {
      await axios.post(`${API}/parlays`, { legs: out, leg_count: out.length, descriptor: `NBA ${date || ''} suggested ticket` });
      toast.success('NBA ticket added to the tracker');
    } catch (e) { toast.error(`Could not add: ${e.response?.data?.detail || e.message}`); }
  };
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/20 p-3" data-testid="nba-suggested-ticket">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm font-bold text-emerald-300">Tonight's suggested NBA ticket — {legs.length} picks</p>
        <button type="button" onClick={track} className="inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400" data-testid="nba-track-ticket">
          <Ticket className="w-3.5 h-3.5" />Track this ticket
        </button>
      </div>
      {mode !== 'pro' && <div className="text-[12px] text-zinc-400 mb-2 space-y-1" data-testid="nba-ticket-explainer">
        <p><b className="text-zinc-200">How it works:</b> a ticket is a list of things you predict will happen in tonight's games. You only win if <b className="text-zinc-200">every one</b> of them comes true.</p>
        <p>We picked things each player normally does with ease, like scoring 10 or more points, and set the bar below what they usually do. The <b className="text-emerald-300">% likely</b> is our chance it happens. Healthy players only, at most 2 picks per game.</p>
      </div>}
      {legs.map((x, i) => <NbaPlainLeg key={x.key || `${x.player}-${x.stat}`} x={x} i={i} />)}
    </div>
  );
};

const NbaLearned = ({ notes }) => (
  <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-950/20 p-3" data-testid="nba-learned">
    <p className="text-xs font-bold text-fuchsia-300 flex items-center gap-1"><Brain className="w-3.5 h-3.5" />What the picks learned</p>
    {notes?.length ? <ul className="mt-1 space-y-0.5">{notes.map((n) => <li key={n} className="text-[11px] text-zinc-300">• {n}</li>)}</ul>
      : <p className="text-[11px] text-zinc-400 mt-0.5">Still collecting results — after about 10 graded picks per stat, lines that keep missing get lowered automatically. Preseason games never count.</p>}
  </div>
);

// NBA: its own section — nightly safe tickets, team wins, player props per game.
export default function NbaPicksPage() {
  const [board, setBoard] = useState(null);
  const [parlays, setParlays] = useState([]);
  useEffect(() => {
    axios.get(`${API}/board`).then((r) => setBoard(r.data)).catch(() => setBoard({ error: true }));
    axios.get(`${API}/parlays`).then((r) => setParlays(r.data.parlays || [])).catch(() => {});
  }, []);
  const idx = useMemo(() => ticketIndex(parlays), [parlays]);
  if (sessionStorage.getItem('sports_betting_authed') !== 'true') return <Navigate to="/sports-betting" replace />;
  const nba = board?.nba;
  const games = nba ? [...nba.games].sort((a, b) => a.date.localeCompare(b.date)) : [];
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-6" data-testid="nba-picks-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/sports-betting" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="nba-back"><ArrowLeft className="w-4 h-4" /> My tickets</Link>
          <div className="text-right">
            <h1 className="text-lg md:text-xl font-bold">🏀 NBA Picks</h1>
            <div className="mt-1 flex justify-end"><BetModeSwitch compact /></div>
            <p className="text-[11px] text-zinc-500">{nba?.date ? `${dayLabel(`${nba.date}T23:00:00Z`)} · ${games.length} games` : 'Nightly slate'}</p>
          </div>
        </div>
        <PicksCalendar league="nba" />
        <TicketBuilder league="nba" />
        <UploadGames league="nba" />
        <PickRulesPanel />
        <PregameStatus leagues={['nba']} />
        {!board && <div className="flex justify-center gap-2 py-16 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" />Loading tonight's games…</div>}
        {board?.error && <p className="text-sm text-red-400">Could not load NBA picks.</p>}
        {nba && (
          <>
            {nba.building && !games.length && <p className="text-xs text-zinc-400" data-testid="nba-building">NBA picks are updating — refresh in a minute.</p>}
            <PicksNotOnTickets league="nba" />
            <NbaSuggestedTicket legs={nba.suggested_ticket} date={nba.date} />
            <NbaLearned notes={nba.learned?.notes} />
            <SafeSection title="NBA nightly safe tickets" tickets={board.safe?.nba || []} league="nba" amount={board.settings?.ticket_amount} />
            <NbaWins wins={nba.wins} />
            <p className="text-[11px] text-zinc-500">Regular-season games only (preseason is ignored). Early season: picks use last season's stats until a player has 5 games, then this season takes over. Tap a game for every player's picks.</p>
            <div className="space-y-2" data-testid="nba-games">
              <GamesBySlot games={games} testId="nba-slots" render={(g) => <GameCard key={g.event_id} g={g} ticketNames={idx.players} />} />
              {!games.length && !nba.building && <p className="text-xs text-zinc-500">No NBA games on the schedule right now.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
